/** Partner page */
import { Grid, makeStyles, Typography } from '@material-ui/core'
import { GetStaticProps } from 'next'
import React from 'react'

import Markdown from '@aletheia/common/components/Markdown'
import PageComponent from '@aletheia/common/components/Page'
import TeamMemberCard from '@aletheia/common/components/Team/TeamMemberCard'
import { getEntry } from '@aletheia/common/lib/contentful'
import { formatTextList } from '@aletheia/common/utils/formatters'

import {
  IPerson,
  fetchPage,
  IPage,
  IFund,
  isEntry,
  fetchSeriesEntries,
} from '../contentful'

const useStyles = makeStyles((theme) => ({
  teamMemberContainer: {
    marginBottom: theme.spacing(4),
  },
  teamMemberWrapper: {
    display: 'flex',
    alignItems: 'stretch',
  },
  bodyMarkdown: {
    '& *': {
      fontFamily: theme.typography.h1.fontFamily,
    },
  },
}))

type TeamPageProps = {
  Team: IPerson[]
  Funds: IFund[]
  Page: IPage
  Advisors: Advisor[]
}

type Advisor = {
  Person: IPerson
  Funds: IFund[]
}

const TeamPage: React.FC<TeamPageProps> = ({ Team, Page, Funds, Advisors }) => {
  const classes = useStyles()
  const { title, body, bannerImage, shortTitle } = Page.fields

  const advisorFundsToSentence = (Advisor: Advisor): string => {
    return formatTextList(Advisor.Funds.map((Fund) => Fund.fields.title))
  }

  return (
    <PageComponent title={title} pageTitle={shortTitle} banner={bannerImage}>
      <Markdown className={classes.bodyMarkdown}>{body}</Markdown>
      <Typography variant="h4" component="h2" gutterBottom>
        EA Funds Team
      </Typography>
      <Grid container spacing={2} className={classes.teamMemberContainer}>
        {Team.map((TeamMember) => (
          <Grid
            item
            xs={12}
            md={6}
            key={TeamMember.sys.id}
            className={classes.teamMemberWrapper}
          >
            <TeamMemberCard
              Person={TeamMember}
              jobTitle={TeamMember.fields.jobTitle || ''}
              longJobTitle={
                <>
                  {TeamMember.fields.jobTitle}{' '}
                  {isEntry(TeamMember.fields.organization) && (
                    <>at {TeamMember.fields.organization.fields.title}</>
                  )}
                </>
              }
            />
          </Grid>
        ))}
      </Grid>
      <Typography variant="h4" component="h2" gutterBottom>
        Our Fund managers
      </Typography>
      {Funds.map((Fund) => (
        <div key={Fund.sys.id}>
          <Typography variant="h5" component="h3" gutterBottom>
            {Fund.fields.title} Fund
          </Typography>
          <Grid container spacing={2} className={classes.teamMemberContainer}>
            {Fund.fields.staffMembers?.filter(isEntry).map((StaffMember) => (
              <Grid
                item
                xs={12}
                md={6}
                key={StaffMember.sys.id}
                className={classes.teamMemberWrapper}
              >
                <TeamMemberCard
                  Person={StaffMember.fields.person}
                  jobTitle={StaffMember.fields.jobTitle}
                  longJobTitle={`${Fund.fields.title} ${StaffMember.fields.jobTitle}`}
                  Organization={StaffMember.fields.person.fields.organization}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      ))}

      <Typography variant="h5" component="h2" gutterBottom>
        Fund advisors
      </Typography>
      <Grid container spacing={2} className={classes.teamMemberContainer}>
        {Advisors.map((Advisor) => (
          <Grid
            item
            xs={12}
            md={6}
            key={Advisor.Person.sys.id}
            className={classes.teamMemberWrapper}
          >
            <TeamMemberCard
              Person={Advisor.Person}
              Organization={getEntry(Advisor.Person.fields.organization)}
              jobTitle={advisorFundsToSentence(Advisor)}
              longJobTitle={`Fund Advisor at ${advisorFundsToSentence(
                Advisor,
              )}`}
            />
          </Grid>
        ))}
      </Grid>
    </PageComponent>
  )
}

export default TeamPage

export const getStaticProps: GetStaticProps<TeamPageProps> = async (_ctx) => {
  const [Page, Team, Funds] = await Promise.all([
    fetchPage('team'),
    fetchSeriesEntries('ea-funds-team'),
    fetchSeriesEntries('ordered-funds'),
  ])

  if (!Page) throw new Error('Missing Page')
  if (!Team) throw new Error('Missing EA Funds Team')
  if (!Funds) throw new Error('Missing Funds')

  // Transforms Funds.Advisors into an array that is inversed => Advisor.Funds
  const Advisors: Advisor[] = []
  Funds.forEach((Fund) => {
    if (Fund.fields.advisors) {
      Fund.fields.advisors.forEach((Person: IPerson) => {
        // Check if user is already in the Advisors array
        const index = Advisors.findIndex((a) => a.Person === Person)
        if (index === -1) {
          // Person is not in the Advisors array -> Add Person and initial Fund to the list
          Advisors.push({
            Person: Person,
            Funds: [Fund],
          })
        } else {
          // Person is already in the Advisors array -> Append only the Fund
          Advisors[index].Funds.push(Fund)
        }
      })
    }
  })

  return {
    props: { Team, Page, Funds, Advisors },
    revalidate: 1 * 60,
  }
}
